// Mobile bottom sheet — the touch-native replacement for edge-anchored
// popovers and centred dialogs on small screens. Slides up from the
// bottom, scrim-dismissable, Escape-dismissable, locks body scroll, keeps
// keyboard focus inside while open, and pays back the home-indicator
// safe-area inset. Rendered in a portal on document.body so it escapes any
// transformed/overflow-clipped ancestor.
//
// Pair with useIsMobile to choose sheet-on-mobile / popover-on-desktop:
//
//   const isMobile = useIsMobile();
//   return isMobile
//     ? <Sheet open={open} onClose={close} title="Notifications">…</Sheet>
//     : <Popover>…</Popover>;
//
// Modal renders through this on phones.

import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { Button } from "./Button";
import { OVERLAY_SHADOW, Z, cx } from "./controlStyles";
import { isTopLayer, useFocusTrap, useScrollLock } from "./overlayHooks";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  /** Controls in the title row, beside Close (a "⋯" menu). */
  actions?: ReactNode;
  children: ReactNode;
  /** Sticky action row at the bottom (e.g. Cancel / Save). */
  footer?: ReactNode;
  /** Cap the sheet height as a fraction of the dynamic viewport. */
  maxHeightDvh?: number;
  /** Padding/util classes for the scroll body. Override to e.g. "pb-2"
   * when the children supply their own horizontal padding (lists). */
  contentClassName?: string;
  /** false = no scrim click / Escape / close button (a running action). */
  dismissible?: boolean;
  /** Element to focus when the sheet opens (default: the first control of the body). */
  initialFocus?: RefObject<HTMLElement>;
  /** Extra classes for the sheet panel. */
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  actions,
  children,
  footer,
  maxHeightDvh = 0.9,
  contentClassName = "px-5 pb-5",
  dismissible = true,
  initialFocus,
  className,
}: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useScrollLock(open);
  useFocusTrap(panel, open, initialFocus);

  // Innermost layer only (see Modal), and not when a menu or list inside
  // already handled the key.
  useEffect(() => {
    if (!open || !dismissible) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented || !isTopLayer(panel.current)) return;
      event.preventDefault();
      onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, dismissible]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center bg-[color:var(--c-scrim)] backdrop-blur-sm"
      style={{ zIndex: Z.sheet }}
      onClick={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cx(
          "latticeSheetIn flex w-full flex-col rounded-t-card border border-b-0 border-line bg-overlay outline-none sm:max-w-[480px]",
          OVERLAY_SHADOW,
          className,
        )}
        style={{
          maxHeight: `${Math.round(maxHeightDvh * 100)}dvh`,
          // In the installed PWA env() reads 0 without viewport-fit=cover;
          // the companion publishes its measured inset as --app-inset-b.
          paddingBottom: "max(var(--app-inset-b, 0px), env(safe-area-inset-bottom, 0px))",
        }}
      >
        {/* Grab handle — signals "drag/dismiss" and centres the eye. */}
        <div className="flex shrink-0 justify-center pb-1 pt-2.5">
          <span aria-hidden className="h-1 w-9 rounded-pill bg-[color:var(--c-border-strong)]" />
        </div>
        {title !== undefined && (
          <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-1">
            <div className="min-w-0">
              <h2 id={titleId} className="truncate text-md font-semibold text-fg">
                {title}
              </h2>
              {description && (
                <p id={descId} className="mt-0.5 text-sm text-fg-muted">
                  {description}
                </p>
              )}
            </div>
            {(actions || dismissible) && (
              <div className="-mr-2 -mt-1 flex shrink-0 items-center gap-1">
                {actions}
                {dismissible && (
                  <Button variant="ghost" size="icon-lg" onClick={onClose} aria-label="Close">
                    <X aria-hidden className="h-5 w-5" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        <div data-dialog-body="" className={cx("min-h-0 flex-1 overflow-y-auto", contentClassName)}>
          {children}
        </div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line-subtle px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
